import {
  type PreparedShape,
  type Rng,
  type ShapeDefinition,
  type ShapeSink,
  type Vec3,
} from '../shape.js';
import { alongSegment, hash01, onSphere, ramp, revolve, TAU } from './helpers.js';

/* ── Butterfly ─────────────────────────────────────────────────────────── */

/** Wing outline: a fat upper lobe and a smaller lower lobe, per side. */
function wingPoint(sink: ShapeSink, random: Rng, side: number, isUpper: boolean): void {
  const root = isUpper ? 0.15 : 0.95;
  const angle = root + random() * (isUpper ? 1.15 : 0.75);
  const reach = isUpper ? 5.6 : 3.6;
  // The sine can dip a hair below zero at the far edge of the sweep, and a
  // negative base under a fractional power is NaN.
  const lobe = Math.max(0, Math.sin((angle - root) * (isUpper ? 2.7 : 4.2))) ** 0.6;
  const r = Math.sqrt(random()) * reach * lobe;
  sink.x = side * Math.cos(angle - 0.35) * r;
  sink.y = 4 + Math.sin(angle - 0.35) * r * (isUpper ? 1 : -1.15);
  sink.z = Math.abs(sink.x) * 0.06;
}

export const butterfly: ShapeDefinition = {
  name: 'butterfly',
  palette: { base: 0x4e_20_54, shade: 0xff_7a_3a, accent: 0x9c_e8_ff },
  driftRate: 1.9,
  framing: 2.56,
  prepare: ({ random, total, variant }): PreparedShape => {
    const spots = 2 + Math.floor(variant * 3);
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.86) {
        const side = random() < 0.5 ? -1 : 1;
        wingPoint(sink, random, side, p < 0.58);
        const out = Math.abs(sink.x) / 5.6;
        // Concentric bands plus a ring of eyespots near the wing edge.
        const band = Math.abs(Math.sin(out * 7 + sink.y * 0.5));
        const isEye =
          Math.abs(Math.sin(Math.atan2(sink.y - 4, sink.x) * spots)) > 0.97 && out > 0.55;
        sink.tint = isEye ? 0.95 : 0.12 + band * 0.55 * ramp(out, 0.1, 0.9);
        // The flap: wing tips travel, the root stays put.
        sink.driftY = out * out * 2.4;
        sink.driftX = -side * out * out * 0.7;
      } else if (p < 0.96) {
        // Body: three tapered segments.
        revolve(sink, random, (v) => 0.42 * Math.sin(v * Math.PI) ** 0.6 * (1 - v * 0.35), 6.4);
        sink.y += 1.1;
        sink.z *= 1.3;
        sink.tint = 0.06 + random() * 0.08;
      } else {
        // Antennae, curling forward.
        const side = random() < 0.5 ? -1 : 1;
        const t = random();
        sink.x = side * t * 0.9;
        sink.y = 4 + 1.1 + t * 1.4;
        sink.z = 2.4 + Math.sin(t * 1.2) * 0.6;
        sink.tint = 0.5;
      }
    };
    return { place };
  },
};

/* ── Flock ─────────────────────────────────────────────────────────────── */

interface Bird {
  readonly at: Vec3;
  readonly heading: number;
  readonly size: number;
}

function buildFlock(random: Rng, variant: number): Bird[] {
  const birds: Bird[] = [];
  const count = 130 + Math.floor(variant * 90);
  for (let i = 0; i < count; i += 1) {
    // The flock rides a slow lissajous ribbon, so it reads as one body in flight.
    const t = i / count;
    const along = t * TAU * 1.5;
    birds.push({
      at: {
        x: Math.sin(along) * 9 + (random() - 0.5) * 3.5,
        y: 6 + Math.sin(along * 1.7 + 1) * 3 + (random() - 0.5) * 2.5,
        z: Math.cos(along * 0.8) * 8 + (random() - 0.5) * 3.5,
      },
      heading: along + Math.PI / 2 + (random() - 0.5) * 0.4,
      size: 1 + random() * 0.7,
    });
  }
  return birds;
}

export const flock: ShapeDefinition = {
  name: 'flock',
  palette: { base: 0x38_42_60, shade: 0x7c_8a_b4, accent: 0xff_e8_c0 },
  driftRate: 3.4,
  framing: 2.03,
  prepare: ({ random, total, variant }): PreparedShape => {
    const birds = buildFlock(random, variant);
    const count = birds.length;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.9) {
        const bird = birds[Math.floor(random() * count)];
        if (bird === undefined) {
          return;
        }
        // Each bird is a shallow V: two wings and a short body.
        const side = random() < 0.5 ? -1 : 1;
        const out = random();
        const wing = out * bird.size * 2.4;
        const local = { x: side * wing, y: out * out * bird.size * 0.7, z: -out * bird.size * 0.5 };
        const c = Math.cos(bird.heading);
        const s = Math.sin(bird.heading);
        sink.x = bird.at.x + local.x * c - local.z * s;
        sink.y = bird.at.y + local.y;
        sink.z = bird.at.z + local.x * s + local.z * c;
        sink.tint = 0.05 + out * 0.35;
        sink.driftY = out * out * 1.1;
        sink.phase = bird.heading * 3;
      } else {
        // Sunlit cloud far behind the flock.
        onSphere(sink, random, 16 + random() * 8);
        sink.y = Math.abs(sink.y) * 0.55 + 8;
        sink.tint = 0.8 + random() * 0.2;
        sink.driftX = 0.4;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Whale ─────────────────────────────────────────────────────────────── */

const WHALE_LENGTH = 20;

/** Girth along the body: blunt head, thick middle, narrow peduncle. */
function girth(t: number): number {
  return Math.sin(Math.pow(t, 0.62) * Math.PI) ** 0.75 * 2.5 * (1 - t * 0.55) + 0.08;
}

export const whale: ShapeDefinition = {
  name: 'whale',
  palette: { base: 0x27_45_5e, shade: 0x6a_90_ac, accent: 0xd6_f4_ff },
  driftRate: 0.55,
  framing: 1.89,
  prepare: ({ random, total, variant }): PreparedShape => {
    const arch = 0.5 + variant * 1.4;
    const bodyAt = (t: number): number => Math.sin(t * Math.PI) * arch;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.62) {
        const t = random();
        const angle = random() * TAU;
        const r = girth(t);
        sink.x = (t - 0.5) * WHALE_LENGTH;
        sink.y = 6 + bodyAt(t) + Math.sin(angle) * r;
        sink.z = Math.cos(angle) * r * 1.15;
        // Pale belly, dark back, plus throat pleats on the underside.
        const belly = ramp(-Math.sin(angle), 0.1, 0.9);
        const pleat = t < 0.35 && belly > 0.5 ? Math.abs(Math.sin(sink.x * 3.4)) * 0.25 : 0;
        sink.tint = belly * 0.55 + pleat;
        sink.driftY = ramp(t, 0.55, 1) * 1.6;
        sink.driftX = 0.1;
      } else if (p < 0.78) {
        // Tail flukes.
        const side = random() < 0.5 ? -1 : 1;
        const out = random();
        sink.x = -WHALE_LENGTH / 2 - out * 2.2;
        sink.z = side * out * 4.4;
        sink.y = 6 + bodyAt(0) + out * 0.9 - Math.abs(sink.z) * 0.12;
        sink.tint = 0.18 + out * 0.2;
        sink.driftY = 1.9 + out * 0.8;
      } else if (p < 0.9) {
        // Pectoral fins, long and narrow.
        const side = random() < 0.5 ? -1 : 1;
        const out = random();
        sink.x = -1.5 + out * 3.4;
        sink.z = side * (girth(0.4) + out * 3.6);
        sink.y = 6 + bodyAt(0.42) - out * 1.4;
        sink.tint = 0.6 + out * 0.25;
        sink.driftY = out * 0.7;
      } else {
        // Bubble trail streaming from the blowhole.
        const t = random();
        sink.x = WHALE_LENGTH * 0.32 - t * 6;
        sink.y = 6 + bodyAt(0.85) + 1.4 + t * 7;
        sink.z = (random() - 0.5) * (0.4 + t * 3);
        sink.tint = 0.9 + random() * 0.1;
        sink.driftY = 0.7;
        sink.driftX = 0.3;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Figure: a dancer ──────────────────────────────────────────────────── */

interface Pose {
  readonly shoulder: number;
  readonly elbow: number;
  readonly hip: number;
  readonly knee: number;
  readonly lean: number;
}

function limb(sink: ShapeSink, random: Rng, joints: readonly Vec3[], radius: number): void {
  const link = Math.min(joints.length - 2, Math.floor(random() * (joints.length - 1)));
  const from = joints[link];
  const to = joints[link + 1];
  if (from === undefined || to === undefined) {
    return;
  }
  alongSegment(sink, random, from, to, radius * (1 - link * 0.18));
}

function armJoints(pose: Pose, side: number): Vec3[] {
  const shoulder: Vec3 = { x: side * 1.05, y: 7.4, z: 0 };
  const elbow: Vec3 = {
    x: shoulder.x + side * Math.cos(pose.shoulder) * 1.8,
    y: shoulder.y + Math.sin(pose.shoulder) * 1.8,
    z: side * 0.3,
  };
  const hand: Vec3 = {
    x: elbow.x + side * Math.cos(pose.shoulder + pose.elbow) * 1.7,
    y: elbow.y + Math.sin(pose.shoulder + pose.elbow) * 1.7,
    z: elbow.z + 0.6,
  };
  return [shoulder, elbow, hand];
}

function legJoints(pose: Pose, side: number): Vec3[] {
  const hip: Vec3 = { x: side * 0.72, y: 4.3, z: 0 };
  const knee: Vec3 = {
    x: hip.x + side * Math.sin(pose.hip) * 0.9,
    y: hip.y - Math.cos(pose.hip) * 2.2,
    z: Math.sin(pose.hip) * 1.1,
  };
  const foot: Vec3 = {
    x: knee.x + side * Math.sin(pose.hip + pose.knee) * 0.5,
    y: knee.y - Math.cos(pose.hip + pose.knee) * 2.2,
    z: knee.z + Math.sin(pose.hip + pose.knee) * 1.3,
  };
  return [hip, knee, foot];
}

export const figure: ShapeDefinition = {
  name: 'figure',
  palette: { base: 0x6a_20_60, shade: 0xe8_54_8a, accent: 0xff_e6_ff },
  driftRate: 1.1,
  framing: 2.97,
  prepare: ({ random, total, variant }): PreparedShape => {
    const pose: Pose = {
      shoulder: 0.4 + variant * 2.2,
      elbow: -0.3 - variant * 1.6,
      hip: -0.25 + variant * 0.7,
      knee: 0.2 + variant * 0.8,
      lean: (variant - 0.5) * 0.4,
    };
    const arms = [armJoints(pose, -1), armJoints(pose, 1)];
    const legs = [legJoints(pose, -1), legJoints(pose, 1)];
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.3) {
        // Torso, tapering into the hips.
        revolve(sink, random, (v) => 1.05 - Math.abs(v - 0.45) * 0.5, 3.5);
        sink.z *= 0.62;
        sink.y += 4.2;
        sink.tint = 0.35 + random() * 0.2;
      } else if (p < 0.42) {
        onSphere(sink, random, 0.78);
        sink.y += 8.5;
        sink.tint = 0.55 + random() * 0.15;
      } else if (p < 0.66) {
        limb(sink, random, arms[random() < 0.5 ? 0 : 1] ?? [], 0.34);
        sink.tint = 0.42 + random() * 0.24;
      } else if (p < 0.9) {
        limb(sink, random, legs[random() < 0.5 ? 0 : 1] ?? [], 0.42);
        sink.tint = 0.3 + random() * 0.2;
      } else {
        // Light trailing off her, the way a long exposure smears a dancer.
        onSphere(sink, random, 2.5 + random() * 4);
        sink.y = sink.y * 0.7 + 5;
        sink.tint = 0.9 + random() * 0.1;
        sink.driftX = 0.8;
        sink.driftY = 0.5;
        sink.phase = hash01(index) * TAU;
      }
      sink.x += sink.y * pose.lean * 0.12;
    };
    return { place };
  },
};
